import React, { useMemo, useState } from "react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import {
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import Back from "../../../../../components/Back";
import { useParams } from "react-router-dom";
import { GlobalModal } from "../../../../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import { useServiceProcedureByAgent } from "../../../../../api/catalog_procedure/use-procedures-agent";
import FormattedDateCell from "../../../../../components/FormattedDateCell";

const AgentProcedures = () => {
  const { agentId } = useParams();
  const {
    data: serviceData = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useServiceProcedureByAgent(agentId);

  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [jsonModalData, setJsonModalData] = useState<any>(null);
  const [jsonModalTitle, setJsonModalTitle] = useState("");

  const handleOpenJsonModal = (title: string, data: any) => {
    setJsonModalTitle(title);
    setJsonModalData(data);
    setJsonModalOpen(true);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      { accessorKey: "user_id", header: "User ID" },
      { accessorKey: "catalog_id", header: "Catalog ID" },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = cell.getValue<string>().toUpperCase();
          let chipColor:
            | "default"
            | "primary"
            | "secondary"
            | "error"
            | "info"
            | "success"
            | "warning" = "default";
          switch (status) {
            case "INIT":
              chipColor = "warning";
              break;
            case "IN_PROGRESS":
              chipColor = "info";
              break;
            case "SUCCESS":
              chipColor = "success";
              break;
            case "ERROR":
              chipColor = "error";
              break;
            default:
              chipColor = "default";
          }
          return <Chip label={status} color={chipColor} sx={{ width: 150 }} />;
        },
      },
      { accessorKey: "agent_id", header: "Agent ID" },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 2 }}>
      <Title>Service Procedures for Agent: {agentId}</Title>
      {error && <Typography color="error">Error loading data</Typography>}
      {isLoading && <Typography>Loading procedures...</Typography>}

      <MaterialReactTable
        columns={columns}
        data={serviceData}
        muiTableContainerProps={{ sx: { maxHeight: "calc(78vh - 112px)" } }}
        muiPaginationProps={{
          color: "secondary",
          rowsPerPageOptions: [10, 25, 50, 100],
          shape: "rounded",
          variant: "outlined",
        }}
        paginationDisplayMode="pages"
        columnFilterDisplayMode="popover"
        initialState={{
          pagination: { pageSize: 25, pageIndex: 0 },
          density: "compact",
          showGlobalFilter: true,
          columnPinning: { right: ["mrt-row-actions"] },
          sorting: [{ id: "created_at", desc: true }],
        }}
        enableColumnResizing
        enableGrouping={false}
        enableRowSelection={false}
        enableRowActions={true}
        enableColumnOrdering={false}
        enableColumnPinning={true}
        enableStickyHeader={true}
        enableColumnFilterModes={true}
        positionActionsColumn="last"
        enableFacetedValues={true}
        getRowId={(row) => String(row.id)}
        layoutMode="grid"
        muiToolbarAlertBannerProps={
          error ? { color: "error", children: "Error loading data" } : undefined
        }
        state={{
          isLoading,
          showAlertBanner: !!error,
          showProgressBars: isRefetching,
        }}
        renderTopToolbarCustomActions={() => (
          <Tooltip arrow title="Refresh Data">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
        renderRowActionMenuItems={({ row }) => [
          <MenuItem
            key={0}
            onClick={() => handleOpenJsonModal("Payload", row.original.payload)}
            sx={{ m: 0 }}
          >
            View Payload
          </MenuItem>,
          <MenuItem
            key={1}
            onClick={() => handleOpenJsonModal("Result", row.original.result)}
            sx={{ m: 0 }}
          >
            View Result
          </MenuItem>,
        ]}
      />

      <GlobalModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        title={jsonModalTitle}
      >
        <JsonEditor
          minWidth="100%"
          data={jsonModalData}
          restrictAdd
          restrictDelete
          restrictEdit
        />
      </GlobalModal>
      <Back />
    </Box>
  );
};

export default AgentProcedures;
